// Micro Level Monitoring.
//
// Drill-down for a SINGLE area: pick an area and a date range, and get a flat
// chronological list of everything planned and everything that actually ran in
// that room - one row per activity, plan and actual side by side.
//
// The Area Consolidated Tracker answers "how is every area doing?". This answers
// the opposite question: "what happened in Compression - 01 last week, in order?"
//
// It reuses the tracker's endpoint with the area_id filter, so the matching rules
// are identical by construction - same area_id + batch_number match, same
// matched / not_started / unplanned buckets, same variance maths. Manufacturing,
// cleaning and maintenance all appear; the API already returns activity_timeline
// sorted chronologically.
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { DatePicker, Select, Spin, Button, message } from 'antd';
import dayjs from 'dayjs';
import 'antd/dist/reset.css';
import { fetchAreaActivitiesByArea, fetchAreaPlannerAreaList } from './api';

const { RangePicker } = DatePicker;

const activityColors = {
  manufacturing: '#3b82f6',
  cleaning: '#10b981',
  maintenance: '#f59e0b',
};

const statusStyles = {
  matched: { backgroundColor: '#dcfce7', color: '#166534', label: 'Matched' },
  not_started: { backgroundColor: '#fef3c7', color: '#92400e', label: 'Not Started' },
  unplanned: { backgroundColor: '#fee2e2', color: '#991b1b', label: 'Unplanned' },
};

const AreaMicroMonitoring = () => {
  // Default range: today rounded up to the hour, back 23 hours - same as the tracker
  const calculateDefaultRange = () => {
    const rounded = new Date();
    if (rounded.getMinutes() > 0 || rounded.getSeconds() > 0 || rounded.getMilliseconds() > 0) {
      rounded.setHours(rounded.getHours() + 1);
    }
    rounded.setMinutes(0, 0, 0);
    const end = dayjs(rounded);
    return [end.subtract(23, 'hour'), end];
  };

  const [areaList, setAreaList] = useState([]);
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const [dateTimeRange, setDateTimeRange] = useState(() => calculateDefaultRange());
  const [bucket, setBucket] = useState(null);
  const [areaName, setAreaName] = useState('');
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Areas to choose from. Defaults to the first one so the page is useful on open.
  useEffect(() => {
    const loadAreas = async () => {
      try {
        const response = await fetchAreaPlannerAreaList();
        const areas = response.data || [];
        setAreaList(areas);
        if (areas.length > 0) setSelectedAreaId(areas[0].area_id);
      } catch (err) {
        setError(err.message);
      }
    };
    loadAreas();
  }, []);

  const fetchData = useCallback(async () => {
    if (!selectedAreaId || !dateTimeRange?.[0] || !dateTimeRange?.[1]) return;

    setLoading(true);
    setError(null);
    try {
      const response = await fetchAreaActivitiesByArea({
        start_date: dateTimeRange[0].format('YYYY-MM-DD HH:mm:ss'),
        end_date: dateTimeRange[1].format('YYYY-MM-DD HH:mm:ss'),
        area_id: selectedAreaId,
      });

      // The filter narrows the response to a single bucket, keyed by area name
      const entries = Object.entries(response.data || {});
      setAreaName(entries.length ? entries[0][0] : '');
      setBucket(entries.length ? entries[0][1] : null);
      setMeta(response.meta || null);
    } catch (err) {
      setError(err.message);
      setBucket(null);
    } finally {
      setLoading(false);
    }
  }, [selectedAreaId, dateTimeRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const activities = useMemo(() => bucket?.activity_timeline || [], [bucket]);

  const counts = useMemo(() => ({
    matched: bucket?.matched?.length || 0,
    notStarted: bucket?.not_started?.length || 0,
    unplanned: bucket?.unplanned?.length || 0,
    total: activities.length,
  }), [bucket, activities]);

  const formatStamp = (value) => {
    if (!value) return '-';
    if (value === 'running') return 'Running';
    const d = dayjs(value);
    return d.isValid() ? d.format('DD-MMM HH:mm') : '-';
  };

  const getVarianceStyle = (variance) => {
    if (variance === null || variance === undefined || variance === '' || variance === '-') return {};
    const num = typeof variance === 'string' ? parseFloat(variance) : variance;
    if (isNaN(num)) return {};
    if (num > 0) return { backgroundColor: '#fee2e2', color: '#991b1b', fontWeight: '600' };
    if (num < 0) return { backgroundColor: '#dcfce7', color: '#166534', fontWeight: '600' };
    return { backgroundColor: '#fef3c7', color: '#92400e', fontWeight: '600' };
  };

  const operator = (activity) => {
    const start = activity.start_user || '';
    const end = activity.end_user || '';
    if (!start && !end) return '-';
    if (start && end && start !== end) return `${start} → ${end}`;
    return start || end;
  };

  const handleExport = useCallback(() => {
    if (activities.length === 0) {
      message.warning('Nothing to export for this area and date range');
      return;
    }
    setExporting(true);
    try {
      const escapeCsv = (value) => {
        if (value === null || value === undefined) return '';
        const str = String(value);
        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
      };

      const rows = [];
      rows.push([`Micro Level Monitoring - ${areaName}`].join(','));
      rows.push([`Range: ${dateTimeRange[0].format('DD-MMM-YYYY HH:mm')} to ${dateTimeRange[1].format('DD-MMM-YYYY HH:mm')}`].join(','));
      rows.push([`Unaccounted time: ${bucket?.unutilized_time || '-'}`].join(','));
      rows.push('');
      rows.push([
        '#', 'Activity Type', 'Plan Batch', 'Actual Batch', 'Plan Start', 'Plan End',
        'Actual Start', 'Actual End', 'Plan Hrs', 'Actual Hrs', 'Variance', 'Status', 'By', 'Remarks'
      ].map(escapeCsv).join(','));

      activities.forEach((a, i) => {
        rows.push([
          i + 1,
          a.cleaning_type ? `${a.activity_type} (${a.cleaning_type})` : a.activity_type,
          a.plan_product, a.actual_product,
          formatStamp(a.plan_start_date), formatStamp(a.plan_end_date),
          formatStamp(a.actual_start_time), formatStamp(a.actual_end_time),
          a.sliced_plan_hrs, a.sliced_actual_hrs, a.sliced_time_variance,
          statusStyles[a.status]?.label || a.status,
          operator(a), a.remarks
        ].map(escapeCsv).join(','));
      });

      const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = String(areaName).replace(/[^\w-]+/g, '_');
      link.download = `Area_Micro_Monitoring_${safeName}_${dateTimeRange[0].format('YYYY-MM-DD')}_to_${dateTimeRange[1].format('YYYY-MM-DD')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Exported successfully');
    } catch (err) {
      message.error(`Failed to export: ${err.message || 'Unknown error'}`);
    } finally {
      setExporting(false);
    }
  }, [activities, areaName, dateTimeRange, bucket]);

  const th = (extra = {}) => ({
    backgroundColor: '#1f2937',
    color: 'white',
    padding: '12px 8px',
    textAlign: 'center',
    fontWeight: '600',
    borderBottom: '2px solid #374151',
    position: 'sticky',
    top: 0,
    whiteSpace: 'nowrap',
    ...extra,
  });

  const td = (extra = {}) => ({
    padding: '8px 10px',
    borderBottom: '1px solid #e5e7eb',
    textAlign: 'center',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    ...extra,
  });

  const summaryChip = (label, value, color) => (
    <div style={{
      padding: '8px 14px',
      backgroundColor: 'white',
      border: `1px solid ${color}33`,
      borderLeft: `3px solid ${color}`,
      borderRadius: '6px',
      minWidth: '110px',
    }}>
      <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</div>
      <div style={{ fontSize: '18px', fontWeight: '600', color }}>{value}</div>
    </div>
  );

  return (
    <div style={{ padding: '20px', fontFamily: 'system-ui, -apple-system, sans-serif', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px', backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>
            🔬 Micro Level Monitoring
          </h1>
          {areaName && (
            <span style={{ fontSize: '15px', color: '#374151', fontWeight: '500' }}>
              {areaName}
              <span style={{ color: '#9ca3af', marginLeft: '8px', fontSize: '13px' }}>{selectedAreaId}</span>
            </span>
          )}
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>Area:</label>
            <Select
              showSearch
              value={selectedAreaId}
              onChange={setSelectedAreaId}
              style={{ minWidth: 240 }}
              placeholder="Select an area"
              optionFilterProp="label"
              options={areaList.map(a => ({ value: a.area_id, label: `${a.area_name} (${a.area_id})` }))}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>Date Range:</label>
            <RangePicker
              showTime={{ format: 'HH:mm', minuteStep: 5 }}
              format="DD-MMM-YY HH:mm"
              value={dateTimeRange}
              onChange={(dates) => dates && setDateTimeRange(dates)}
              allowClear={false}
              style={{ minWidth: '340px' }}
              presets={[
                { label: 'Last 24 Hours', value: [dayjs().subtract(24, 'hours'), dayjs()] },
                { label: 'Last 7 Days', value: [dayjs().subtract(7, 'day').startOf('day'), dayjs()] },
                { label: 'Last 30 Days', value: [dayjs().subtract(30, 'day').startOf('day'), dayjs()] },
                { label: 'This Week', value: [dayjs().startOf('week'), dayjs()] },
                { label: 'This Month', value: [dayjs().startOf('month'), dayjs()] },
              ]}
            />
          </div>

          <Button onClick={fetchData} disabled={loading} style={{ fontSize: '13px' }}>
            {loading ? '⏳ Loading...' : '🔄 Refresh'}
          </Button>

          <Button
            type="primary"
            onClick={handleExport}
            loading={exporting}
            disabled={loading || activities.length === 0}
            style={{ backgroundColor: '#10b981', borderColor: '#10b981', marginLeft: 'auto' }}
          >
            📥 Export to Excel
          </Button>
        </div>
      </div>

      {/* Summary */}
      {bucket && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {summaryChip('Activities', counts.total, '#374151')}
          {summaryChip('Matched', counts.matched, '#16a34a')}
          {summaryChip('Not Started', counts.notStarted, '#d97706')}
          {summaryChip('Unplanned', counts.unplanned, '#dc2626')}
          {summaryChip('Unaccounted', bucket.unutilized_time || '-', '#7c3aed')}
          {summaryChip('Time Variance', bucket.production_variance != null ? `${bucket.production_variance} hrs` : '-', '#0891b2')}
          {summaryChip('Volume Variance', bucket.production_volume_variance ?? '-', '#db2777')}
        </div>
      )}

      {error && (
        <div style={{ marginBottom: '20px', backgroundColor: '#fee2e2', border: '1px solid #ef4444', borderRadius: '8px', padding: '16px', color: '#991b1b' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Sequential activity list */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 340px)' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '13px' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 20 }}>
              <tr>
                <th style={th({ width: '48px', left: 0, zIndex: 30 })}>#</th>
                <th style={th({ textAlign: 'left' })}>Activity</th>
                <th style={th()}>Plan Batch</th>
                <th style={th()}>Actual Batch</th>
                <th style={th()}>Plan Start</th>
                <th style={th()}>Plan End</th>
                <th style={th()}>Actual Start</th>
                <th style={th()}>Actual End</th>
                <th style={th()}>Plan Hrs</th>
                <th style={th()}>Actual Hrs</th>
                <th style={th()}>Variance</th>
                <th style={th()}>Status</th>
                <th style={th()}>By</th>
                <th style={th({ textAlign: 'left' })}>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="14" style={{ padding: '48px', textAlign: 'center' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: '16px', color: '#6b7280' }}>Loading activities...</div>
                  </td>
                </tr>
              ) : activities.length === 0 ? (
                <tr>
                  <td colSpan="14" style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>
                    {selectedAreaId
                      ? 'No activity in this area for the selected date range'
                      : 'Select an area to begin'}
                  </td>
                </tr>
              ) : activities.map((activity, idx) => {
                const colour = activityColors[activity.activity_type] || '#6b7280';
                const status = statusStyles[activity.status] || {};
                return (
                  <tr key={`${activity.plan_id || 'p'}-${activity.actual_id || 'a'}-${idx}`}
                      style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                    <td style={td({ color: '#9ca3af', fontWeight: '600' })}>{idx + 1}</td>
                    <td style={td({ textAlign: 'left' })}>
                      <span style={{
                        display: 'inline-block', width: '10px', height: '10px', borderRadius: '3px',
                        backgroundColor: colour, marginRight: '8px', verticalAlign: 'middle',
                      }} />
                      <span style={{ textTransform: 'capitalize', color: '#374151' }}>
                        {activity.activity_type}
                        {activity.cleaning_type && (
                          <span style={{ color: '#6b7280' }}> · {activity.cleaning_type}</span>
                        )}
                      </span>
                    </td>
                    <td style={td()}>{activity.plan_product || '-'}</td>
                    <td style={td()}>{activity.actual_product || '-'}</td>
                    <td style={td({ color: '#6b7280' })}>{formatStamp(activity.plan_start_date)}</td>
                    <td style={td({ color: '#6b7280' })}>{formatStamp(activity.plan_end_date)}</td>
                    <td style={td({ color: '#6b7280' })}>{formatStamp(activity.actual_start_time)}</td>
                    <td style={td({
                      color: activity.actual_end_time === 'running' ? '#2563eb' : '#6b7280',
                      fontWeight: activity.actual_end_time === 'running' ? '600' : '400',
                    })}>
                      {formatStamp(activity.actual_end_time)}
                    </td>
                    <td style={td()}>{activity.sliced_plan_hrs || '-'}</td>
                    <td style={td()}>{activity.sliced_actual_hrs || '-'}</td>
                    <td style={td(getVarianceStyle(activity.sliced_time_variance))}>
                      {activity.sliced_time_variance ?? '-'}
                    </td>
                    <td style={td()}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '500',
                        backgroundColor: status.backgroundColor, color: status.color,
                      }}>
                        {status.label || activity.status}
                      </span>
                    </td>
                    <td style={td({ color: '#6b7280' })}>{operator(activity)}</td>
                    <td style={td({
                      textAlign: 'left', color: '#6b7280', maxWidth: '180px',
                      overflow: 'hidden', textOverflow: 'ellipsis',
                    })} title={activity.remarks || ''}>
                      {activity.remarks || '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {meta && (
        <div style={{ marginTop: '12px', fontSize: '12px', color: '#9ca3af' }}>
          Matched on {meta.match_key} · plan v{meta.plan_version_used} · actuals from {meta.actual_source}
        </div>
      )}
    </div>
  );
};

export default AreaMicroMonitoring;
